/**
 * PHOTOS §1: the two calls to the save server's photo endpoints, in the shape of `cloudSave/transport.ts` - nothing
 * here throws and nothing here logs. A server that is missing, asleep or answering the game's HTML is an ordinary
 * outcome: the picture stays on this device and goes up another time.
 */

import type { FetchLike } from '../cloudSave/types';

const REQUEST_TIMEOUT_MS = 15000;

export type UploadResult =
  | { kind: 'ok' }
  /** The server understood and said no. 403: it holds no save under the code yet - worth trying after one. */
  | { kind: 'refused'; status: number }
  | { kind: 'unreachable' };

export type DownloadResult =
  | { kind: 'ok'; jpeg: Blob }
  /** The server holds no such picture under this code: never uploaded, or dropped past the cap. */
  | { kind: 'none' }
  | { kind: 'unreachable' };

export interface PhotoTransport {
  put(code: string, id: string, jpeg: Blob): Promise<UploadResult>;
  get(code: string, id: string): Promise<DownloadResult>;
}

function timeout(ms: number): AbortSignal | undefined {
  const able = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function';
  return able ? AbortSignal.timeout(ms) : undefined;
}

/** A disk budget that is full (507) stays full for a while: not worth re-sending the picture after every save. */
const transient = (status: number): boolean => (status >= 500 && status !== 507) || status === 429 || status === 408;

/** Starts FF D8: a JPEG, and not the game's HTML from a host that answers every path with it. */
const isJpeg = (bytes: Uint8Array): boolean => bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8;

export function createPhotoTransport(fetchLike: FetchLike, origin: string): PhotoTransport {
  const url = (id: string): string => `${origin}/api/photo/${encodeURIComponent(id)}`;

  return {
    async put(code, id, jpeg) {
      try {
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(url(id), {
          method: 'PUT',
          headers: { Authorization: `Bearer ${code}`, 'Content-Type': 'image/jpeg' },
          body: jpeg,
          cache: 'no-store',
          ...(signal ? { signal } : {}),
        });
        if (res.ok) return { kind: 'ok' };
        return transient(res.status) ? { kind: 'unreachable' } : { kind: 'refused', status: res.status };
      } catch {
        return { kind: 'unreachable' };
      }
    },

    async get(code, id) {
      try {
        const signal = timeout(REQUEST_TIMEOUT_MS);
        const res = await fetchLike(url(id), {
          headers: { Authorization: `Bearer ${code}` },
          cache: 'no-store',
          ...(signal ? { signal } : {}),
        });
        if (res.status === 404) return { kind: 'none' };
        if (!res.ok) return { kind: 'unreachable' };
        const bytes = new Uint8Array(await res.arrayBuffer());
        if (!isJpeg(bytes)) return { kind: 'unreachable' };
        return { kind: 'ok', jpeg: new Blob([bytes], { type: 'image/jpeg' }) };
      } catch {
        return { kind: 'unreachable' };
      }
    },
  };
}
